import { useRequiredContext } from "@redotech/react-util/context";
import {
  createPickedFlatSettingsSchema,
  FlatTeamSettings,
  UpdateFlatTeamSettings,
} from "@redotech/redo-model/flat-team-settings";
import { StandardSchemaV1 } from "@standard-schema/spec";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { ZodType } from "zod/v4";
import { RedoMerchantRpcClientContext } from "../rpc-client";
import { useAppForm } from "./use-app-form";

export function usePickedTeamSettingsForm<K extends keyof FlatTeamSettings>(
  teamId: string | undefined,
  defaults: NOOP<Pick<FlatTeamSettings, K>>,
  onAfterSave?: (values: NOOP<Pick<FlatTeamSettings, K>>) => Promise<void>,
) {
  const { pickedSchema, query, mutation } = usePickedTeamSettings(
    teamId,
    Object.fromEntries(Object.keys(defaults).map((key) => [key, 1])) as Record<
      K,
      1
    >,
  );
  const pickedSchemaNoop = pickedSchema as ZodType<
    NOOP<Pick<FlatTeamSettings, K>>
  >;
  // Merge defaults with query data
  // Prefer query data keys when available
  const values: NOOP<Pick<FlatTeamSettings, K>> = query.data ?? defaults;
  if (query.data) {
    for (const key of Object.keys(defaults)) {
      const keyTyped = key as K;
      values[keyTyped] = values[keyTyped] ?? defaults[keyTyped];
    }
  }
  const form = useAppForm({
    defaultValues: values,
    validators: {
      onChange: pickedSchemaNoop as StandardSchemaV1<
        NOOP<Pick<FlatTeamSettings, K>>,
        unknown
      >,
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(value);
      form.reset(value);
      await onAfterSave?.(value);
    },
  });

  return { pickedSchema, query, mutation, form };
}

// noop forces the intellisense to resolve, instead of listing everything in the giant pre-pick type
// See https://stackoverflow.com/questions/69976130/can-i-force-typescript-to-resolve-the-type-of-pickt-k-for-display
export type NOOP<T> = T extends (...args: any[]) => any
  ? T
  : T extends abstract new (...args: any[]) => any
    ? T
    : { [K in keyof T]: T[K] };

/**
 * A hook to fetch and validate a picked subset of team settings.
 * It uses react query to manage the fetching and caching of the settings.
 *
 * @param teamId The ID of the team to fetch settings for.
 * @param keys The specific settings keys to fetch.
 * @returns The picked schema and query information.
 */
export function usePickedTeamSettings<K extends keyof FlatTeamSettings>(
  teamId: string | undefined,
  keys: Record<K, 1>,
) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  const pickedSchema = useMemo(
    () => createPickedFlatSettingsSchema(Object.keys(keys) as K[]),
    [keys],
  );

  const query = useQuery({
    queryKey: ["teamSettings", teamId, keys] as const,
    queryFn: async ({ signal }) => {
      if (!teamId) throw new Error("Missing teamId");
      const result = await rpcClient.getPickedSettings(
        { teamId, keys },
        { signal },
      );
      const parsed = pickedSchema.safeParse(result.settings);
      if (!parsed.success) {
        throw new Error(
          `Failed to parse team settings: ${parsed.error.message}`,
        );
      }
      return parsed.data as NOOP<Pick<FlatTeamSettings, K>>;
    },
    enabled: !!teamId,
  });

  const mutation = useMutation({
    mutationFn: async (settings: NOOP<Pick<UpdateFlatTeamSettings, K>>) => {
      if (!teamId) throw new Error("Missing teamId");
      // convert all keys that are undefined to null
      // Since we pick out only the keys we want to update, we can safely treat undefined and null as an "unset" request
      const nullifiedSettings = Object.fromEntries(
        Object.entries(settings).map(([key, value]) => [key, value ?? null]),
      ) as Pick<UpdateFlatTeamSettings, K>;
      return await rpcClient.updatePickedSettings({
        teamId,
        settings: nullifiedSettings,
      });
    },
    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({
        queryKey: ["teamSettings", teamId, keys],
      });
      const previousData = queryClient.getQueryData([
        "teamSettings",
        teamId,
        keys,
      ]);
      queryClient.setQueryData(["teamSettings", teamId, keys], (old: any) => ({
        ...old,
        ...newSettings,
      }));
      return { previousData };
    },
    onError: (err, newSettings, context) => {
      queryClient.setQueryData(
        ["teamSettings", teamId, keys],
        context?.previousData,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["teamSettings", teamId],
      });
    },
  });

  return { pickedSchema, query, mutation };
}
